import React, { memo, useState } from 'react';
import { useBundle } from '@amzn/react-arb-tools';
import {
  Checkbox,
  CountrySelect,
  HiddenField,
} from '@xbcb/form-item-components';
import { NamePath } from '@xbcb/ui-types';
import { safeGetMessage } from '@xbcb/ui-utils';
import { Form, Row } from 'antd';
import DecimalQuantity from 'components/DecimalQuantity';
import { useTheme } from 'styled-components';
import { WeightUnit } from '__generated__/graphql';

export type CopperSmeltAndCastCountryDetailProps = {
  localNamePath: NamePath;
  fullNamePath: NamePath;
};

export const primaryCountryOfSmeltLabel = 'Primary Country of Smelt';
export const secondaryCountryOfSmeltLabel = 'Secondary Country of Smelt';
export const countryOfCastLabel = 'Country of Cast';

const notApplicableCode = 'N/A';
const otherUnknownCode = 'OTH';

const additionalCountryOptions = [
  {
    key: notApplicableCode,
    value: notApplicableCode,
    display: 'Not Applicable',
  },
  {
    key: otherUnknownCode,
    value: otherUnknownCode,
    display: 'Other (Unknown)',
  },
];

export enum Section232CopperRequirementsFields {
  PRIMARY_COUNTRY_OF_SMELT = 'copperPrimaryCountryOfSmelt',
  SECONDARY_COUNTRY_OF_SMELT = 'copperSecondaryCountryOfSmelt',
  COUNTRY_OF_CAST = 'copperCountryOfCast',
}

const copperRequirementsFieldPrefix = 'copperRequirements';

const doesNotContainCopperField = 'doesNotContainCopper';
export const doesNotContainCopperLabel = 'Does not contain copper';

const netCopperWeightFieldPrefix = 'netCopperWeight';
export const totalCopperNetWeightLabel = 'Total Copper Net Weight';

export const section232CopperRequirementsRequiredFieldsNamePaths: NamePath[] =
  Object.values(Section232CopperRequirementsFields).map((field: string) => [
    copperRequirementsFieldPrefix,
    field,
  ]);

export const CopperSmeltAndCastCountryDetail: React.FC<
  CopperSmeltAndCastCountryDetailProps
> = ({ localNamePath, fullNamePath }) => {
  const theme = useTheme();
  const [bundle] = useBundle('components.CopperSmeltAndCastCountryDetail');
  const fieldLocalNamePath = (field: Section232CopperRequirementsFields) => [
    ...localNamePath,
    copperRequirementsFieldPrefix,
    field,
  ];

  const form = Form.useFormInstance();

  const [doesNotContainCopper, setDoesNotContainCopper] = useState(() =>
    Boolean(form.getFieldValue([...fullNamePath, doesNotContainCopperField])),
  );

  return (
    <div>
      <Row>
        <Checkbox
          localNamePath={[...localNamePath, doesNotContainCopperField]}
          fullNamePath={[...fullNamePath, doesNotContainCopperField]}
          form={form}
          text={doesNotContainCopperLabel}
          onChange={(event) => {
            setDoesNotContainCopper(event.target.checked);
            form.setFields([
              {
                name: [...fullNamePath, doesNotContainCopperField],
                value: event.target.checked,
              },
              {
                name: [...fullNamePath, copperRequirementsFieldPrefix],
                value: undefined,
              },
            ]);
          }}
        />
      </Row>
      {!doesNotContainCopper && (
        <>
          <CountrySelect
            label={primaryCountryOfSmeltLabel}
            localNamePath={fieldLocalNamePath(
              Section232CopperRequirementsFields.PRIMARY_COUNTRY_OF_SMELT,
            )}
            required
            additionalOptions={additionalCountryOptions}
          />
          <CountrySelect
            label={secondaryCountryOfSmeltLabel}
            localNamePath={fieldLocalNamePath(
              Section232CopperRequirementsFields.SECONDARY_COUNTRY_OF_SMELT,
            )}
            required
            additionalOptions={additionalCountryOptions}
          />
          <CountrySelect
            label={countryOfCastLabel}
            localNamePath={fieldLocalNamePath(
              Section232CopperRequirementsFields.COUNTRY_OF_CAST,
            )}
            required
            additionalOptions={additionalCountryOptions}
          />
          <DecimalQuantity
            label={safeGetMessage(
              bundle,
              'total_copper_net_weight',
              undefined,
              totalCopperNetWeightLabel,
            )}
            field={[
              ...localNamePath,
              copperRequirementsFieldPrefix,
              netCopperWeightFieldPrefix,
              'value',
            ]}
            units="kg"
            $itemSize={theme.size.tiny_short}
          />
          <HiddenField
            localNamePath={[
              ...localNamePath,
              copperRequirementsFieldPrefix,
              netCopperWeightFieldPrefix,
              'unit',
            ]}
            fullNamePath={[
              ...fullNamePath,
              copperRequirementsFieldPrefix,
              netCopperWeightFieldPrefix,
              'unit',
            ]}
            value={WeightUnit.KILOGRAM}
            form={form}
          />
        </>
      )}
    </div>
  );
};

export default memo(CopperSmeltAndCastCountryDetail);
